import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import './StudentLogin.css';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function StudentLogin() {
  const navigate = useNavigate();
  const query = useQuery();
  const returnTo = query.get('return_to') || '/courses';

  const [mode, setMode] = useState('login'); // 'login' | 'change'
  const [form, setForm] = useState({ email: '', password: '' });
  const [changeForm, setChangeForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // If already signed in, bounce. Also: if must_change_password, switch to change view.
  useEffect(() => {
    let active = true;
    api.studentMe()
      .then((me) => {
        if (!active) return;
        if (me.must_change_password) {
          setMode('change');
        } else {
          navigate(returnTo, { replace: true });
        }
      })
      .catch(() => { /* anonymous — stay on login */ });
    return () => { active = false; };
  }, [navigate, returnTo]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const me = await api.studentLogin(form);
      if (me.must_change_password) {
        setMode('change');
        setChangeForm({ ...changeForm, current_password: form.password });
      } else {
        navigate(returnTo, { replace: true });
      }
    } catch (err) {
      setError(err.message || 'Sign in failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = async (e) => {
    e.preventDefault();
    setError('');
    if (changeForm.new_password.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }
    if (changeForm.new_password !== changeForm.confirm_password) {
      setError('New passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await api.studentChangePassword({
        current_password: changeForm.current_password,
        new_password: changeForm.new_password,
      });
      navigate(returnTo, { replace: true });
    } catch (err) {
      setError(err.message || 'Could not update password.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="student-login">
      <div className="container">
        <div className="student-login__card">
          <p className="student-login__crumbs"><Link to="/courses">← Back to courses</Link></p>
          <h1 className="student-login__title">{mode === 'change' ? 'Set a new password' : 'Student sign in'}</h1>
          <p className="student-login__sub">
            {mode === 'change'
              ? 'Pick a password you\'ll remember — this replaces the auto-generated one we emailed you.'
              : 'Use the credentials we emailed you when your account was created.'}
          </p>

          {error && <div className="student-login__error">{error}</div>}

          {mode === 'login' ? (
            <form className="student-login__form" onSubmit={handleLogin}>
              <div className="field">
                <label htmlFor="sl-email">Email</label>
                <input id="sl-email" type="email" required maxLength={190}
                       value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} autoComplete="email" />
              </div>
              <div className="field">
                <label htmlFor="sl-pass">Password</label>
                <input id="sl-pass" type="password" required
                       value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} autoComplete="current-password" />
              </div>
              <button type="submit" className="btn-primary student-login__submit" disabled={submitting}>
                {submitting ? 'Signing in…' : 'Sign in'}
              </button>
              <p className="student-login__hint">
                Don't have an account? <Link to="/contact">Contact us</Link> — accounts are created by the admin team.
              </p>
            </form>
          ) : (
            <form className="student-login__form" onSubmit={handleChange}>
              <div className="field">
                <label htmlFor="cp-current">Current password</label>
                <input id="cp-current" type="password" required
                       value={changeForm.current_password}
                       onChange={(e) => setChangeForm({ ...changeForm, current_password: e.target.value })} />
              </div>
              <div className="field">
                <label htmlFor="cp-new">New password (min 8 chars)</label>
                <input id="cp-new" type="password" required minLength={8}
                       value={changeForm.new_password}
                       onChange={(e) => setChangeForm({ ...changeForm, new_password: e.target.value })} />
              </div>
              <div className="field">
                <label htmlFor="cp-confirm">Confirm new password</label>
                <input id="cp-confirm" type="password" required minLength={8}
                       value={changeForm.confirm_password}
                       onChange={(e) => setChangeForm({ ...changeForm, confirm_password: e.target.value })} />
              </div>
              <button type="submit" className="btn-primary student-login__submit" disabled={submitting}>
                {submitting ? 'Saving…' : 'Save new password'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default StudentLogin;
